import Input from '../common/input';
import { Translate, useTranslate } from '~/i18n';
import { useForm } from 'react-hook-form';
import Axios from '~/utils/axios';
import { useState } from 'react';
import validator from 'validator';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import Loading from '../common/loaders/loader';
import Button from '../common/button';
import toast from 'react-hot-toast';
import {
   businessBottomRequestBtnEvent,
   businessRequestCallSuccessEvent,
   businessRequestCallFailureEvent,
} from '~/utils/mixpanel-events/business';
import useMedia from '~/hooks/useMedia';
import { sentryCaptureException } from '~/errors/sentry';

type SubmitObject = {
   name: string;
   phone_number: string;
   company_name: string;
   email: string;
};

export const TopSectionContactForm = () => {
   const contactList = [
      {
         text: <Translate id="tickets:contact bullet 1" />,
      },
      {
         text: <Translate id="tickets:contact bullet 2" />,
      },
      {
         text: <Translate id="tickets:contact bullet 3" />,
      },
   ];

   const { translate, lang } = useTranslate();
   const { register, handleSubmit, reset, getValues, errors, setError } = useForm<SubmitObject>({
      mode: 'onChange',
   });

   const [loading, setLoading] = useState(false);
   const values = getValues();
   const [success, setSuccess] = useState(false);
   const [captcha, setCaptcha] = useState('');
   const isMobile = useMedia(1100);

   // const fillAgain = () => {
   //    setSuccess(false);
   //    reset();
   // };

   const submitHandler = async (obj: SubmitObject) => {
      if (loading) return;
      setLoading(true);
      const trackObj = {
         name: obj?.name,
         email: obj?.email,
         mobile: obj?.phone_number,
         business_name: obj?.company_name,
      };
      businessBottomRequestBtnEvent(trackObj);
      try {
         const locale = lang?.split('-')[1];
         const { data } = await Axios.post('/business-email', {
            ...obj,
            captcha,
            lang: locale,
         });
         if (data.status === 'Success') {
            businessRequestCallSuccessEvent(trackObj);
            setLoading(false);
            setSuccess(true);
            reset();

            toast.success(translate({ id: 'business:success toaster' }));
         } else {
            if (data.error) {
               Object.entries(data.error).map(([key, value], idx) => {
                  setError(key as any, { message: value + '', shouldFocus: idx === 0 });
               });
            }
            throw new Error(translate({ id: 'business:failed toaster' }));
         }
      } catch (error: any) {
         businessRequestCallFailureEvent(trackObj);
         toast.error(error.message);
         sentryCaptureException(error);
      } finally {
         setLoading(false);
      }
   };

   return (
      <div
         className={`flex-col flex-1 ${
            isMobile ? '' : 'max-w-[370px]'
         } !bg-[#1D4ED8] rounded-xl py-8 ${success ? 'justify-center flex' : ''}`}
         style={
            success
               ? {
                    backgroundColor: '#1D4ED8',
                    background:
                       'url("/images/tickets-page/submit-left-bg.svg") left top no-repeat, url("/images/tickets-page/submit-right-bg.svg") right top no-repeat',
                 }
               : {}
         }>
         {success ? (
            <div className="flex flex-col items-center mt-8 text-center space-y-8">
               <div className="flex flex-col items-center w-full py-8 -mt-16 pb-0">
                  <div className="space-y-2 px-8">
                     <img src="/images/tickets-page/success-icon.svg" className="m-auto" alt="" />
                     <div className="text-2xl font-medium text-white">
                        <Translate id="business:thankyou" />
                     </div>
                     <div className="text-lg text-white">
                        <Translate id="business:team" />
                     </div>
                     {/* <div
                        className="flex justify-center items-center space-x-2 text-sm text-white cursor-pointer pt-4"
                        onClick={fillAgain}>
                        <img src="/images/tickets-page/reset-icon.svg" alt="" />
                        <span>Fill again</span>
                     </div> */}
                  </div>
               </div>
               {/* <div className="text-2xl text-white">
                  <Translate id="business:thankyou" />
               </div>
               <Sucess />
               <div className="text-lg text-white">
                  <Translate id="business:team" />
               </div> */}
            </div>
         ) : (
            <div className=" px-8">
               <span className="font-nycd rtl:font-arb flex text-center text-white justify-center text-2xl">
                  <Translate id={`tickets:speak to our Sales Team`} />
               </span>
               <form
                  onSubmit={handleSubmit(submitHandler)}
                  id="request-callback-form"
                  className="mt-6">
                  <Input
                     type="text"
                     placeholder={translate({ id: 'business:enter name' })}
                     className="rounded-lg bg-gray-light !h-12 !px-4"
                     groupClassName="mb-6"
                     id="name"
                     name="name"
                     error={errors.name?.message}
                     valid={!!values.name}
                     innerRef={register({
                        required: translate({ id: 'business:required field' }),
                     })}
                  />
                  <Input
                     type="text"
                     placeholder={translate({ id: 'business:enter mobile number' })}
                     className="rounded-lg bg-gray-light !h-12 !px-4"
                     groupClassName="mb-6"
                     id="phone_number"
                     name="phone_number"
                     inputMode="numeric"
                     error={errors.phone_number?.message && <Translate id="business:validNumber" />}
                     valid={!!values.phone_number}
                     innerRef={register({
                        required: translate({ id: 'business:required field' }),
                     })}
                  />
                  <Input
                     type="text"
                     placeholder={translate({ id: 'business:enter business name' })}
                     className="rounded-lg bg-gray-light !h-12 !px-4"
                     groupClassName="mb-6"
                     id="company name"
                     name="company_name"
                     error={errors.company_name?.message}
                     valid={!!values.company_name}
                     innerRef={register({
                        required: translate({ id: 'business:required field' }),
                     })}
                  />
                  <Input
                     type="email"
                     id="email"
                     name="email"
                     placeholder={translate({ id: 'business:enter email' })}
                     className="rounded-lg bg-gray-light !h-12 !px-4"
                     groupClassName="mb-6"
                     innerRef={register({
                        validate: value =>
                           validator.isEmail(value) || translate({ id: 'auth:invalid email' }),
                        required: translate({ id: 'business:required field' }),
                        pattern: {
                           value: /^((?!hotmail|outlook|gmail|yahoo|live|icloud).)*$/,
                           message: translate({ id: 'auth:email domain mismatch' }),
                        },
                     })}
                     error={errors.email?.message}
                     valid={!!values.email}
                  />
                  <GoogleReCaptcha onVerify={setCaptcha} />
                  <div className="flex justify-center">
                     <Button
                        btnType="submit"
                        className="lg:w-[410px] w-full bg-[#34D399] shadow-xl border-[rgba(53,58,63,0.1)] requestcallback-bottom"
                        disabled={loading}>
                        <div className="!text-white">
                           {loading ? (
                              <Loading color="white" />
                           ) : (
                              <Translate id={`tickets:get in touch`} />
                           )}
                        </div>
                     </Button>
                  </div>
               </form>
            </div>
         )}

         <hr className={`w-32 border-gray-50 m-auto ${success ? '' : 'lg:mt-16 '} my-6`} />

         <div className="space-y-4  px-8">
            {contactList?.map(({ text }, index) => {
               return (
                  <div className="flex items-center gap-4" key={index}>
                     <img src="/images/tickets-page/hand-tick-icon.png" alt="icon" />
                     <span className="text-white uppercase font-bold text-sm ">{text}</span>
                  </div>
               );
            })}
         </div>
      </div>
   );
};
